import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { ThreeDot } from '@/assets';
import {
  IconBoldArrow,
  IconComment,
  IconHeartLiked,
  IconHeartNotLiked,
  IconHidePost,
  IconReportPost,
  IconUnFollow,
} from '@/components/assets';
import AvatarPlaceholder from '@/components/avatar-default';
import { CardRenderFollowType } from '@/components/card';
import { ListSource } from '@/configs/constant';
import { useAuth } from '@/provider/UserProvider';
import { postFollower, postReaction } from '@/services/feed';
import { getDurationAsTimeFromNow } from '@/utils';

import FeedContext from '../FeedContext';
import SharePost from './SharePost';
import TooltipCustom from '@/components/tooltip-custom';

const DynamicInteraction = dynamic(() => import('./Interaction'));
const DynamicPostCardDetail = dynamic(() => import('./PostCardDetail'));

const PostCard: React.FC<any> = ({ post }) => {
  const {
    user,
    createdAt,
    asset,
    text,
    totalReactions,
    id: postId,
    totalComments,
    reactions,
  } = post || {};
  const domain = window.location.hostname;
  const [interactionMore, setInteractionMore] = useState<boolean>(false);
  const [totalCommentValue, setTotalCommentValue] = useState(totalComments);
  const [isHidePost, setIsHidePost] = useState<boolean>(false);
  const [isReaction, setIsReaction] = useState<boolean>(false);
  const [totalReactionValue, setTotalReactionValue] = useState(totalReactions);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const { data, contextActionGenerator } = useContext(FeedContext);
  const isWalletConnect = useMemo(() => !!data?.wallet, [data]);
  const { isLogin } = useAuth();

  const onReaction = useCallback(async () => {
    if (!isReaction) {
      setIsReaction(true);
      if (isLogin) setTotalReactionValue(totalReactionValue + 1);
      await postReaction({ type: 1, postId });
    } else {
      setIsReaction(false);
      if (totalReactionValue > 0 && isLogin) {
        setTotalReactionValue(totalReactionValue - 1);
      }
      await postReaction({ type: 0, postId });
    }
  }, [isWalletConnect, contextActionGenerator, postId, totalReactionValue, isReaction]);

  const onComment = () => {
    if (isLogin) {
      setShowDetail(true);
    } else {
      return toast.error('You must login to continue!');
    }
  };

  const handleUnFollow = async () => {
    const [data] = await postFollower({ id: user.id });
    if (data) {
      contextActionGenerator.setRefreshFeed(true);
    }
  };

  useEffect(() => {
    if (Array.isArray(reactions) && reactions.length === 1) {
      setIsReaction(true);
    }
  }, [reactions]);

  return (
    <>
      {isHidePost ? (
        <div className="flex w-full flex-col items-start gap-3 rounded-2xl bg-gray-50 p-3">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2.5">
              <IconHidePost />
              <span className="text-16 font-semibold text-black">Hidden post</span>
            </div>
            <button
              className="flex items-center justify-center gap-2.5 rounded-[100px] border-1 border-solid border-gray-200 bg-gray-100 px-4 py-2"
              type="button"
              onClick={() => setIsHidePost(false)}
            >
              Undo
            </button>
          </div>
          <div className="h-[1px] w-full bg-gray-200" />
          <div className="flex flex-col items-start gap-3">
            <div
              className="flex items-center gap-2.5 hover:cursor-pointer"
              onClick={() => handleUnFollow()}
            >
              <IconUnFollow />
              <span className="text-14 font-semibold text-black">UnFollow {user.username}</span>
            </div>
            <div className="flex items-center gap-2.5 hover:cursor-pointer">
              <IconReportPost />
              <span className="text-14 font-semibold text-black">Report post</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-5">
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-center justify-between">
              <Link href={`/profile/${user?.publicAddress}`} className="flex items-center gap-3">
                <AvatarPlaceholder
                  src={user?.picture}
                  alt="avatar"
                  address={user?.publicAddress}
                  className="h-12 w-12 rounded-full"
                />
                <div className="flex flex-col">
                  <span className="text-16 font-semibold text-black">{user?.username}</span>
                  <span className="text-13 font-normal text-gray-500">
                    {getDurationAsTimeFromNow(createdAt)}
                  </span>
                </div>
              </Link>

              <div className="relative">
                <div
                  className="h-7 w-7 rounded-full hover:cursor-pointer hover:bg-gray-100"
                  onClick={() => setInteractionMore(!interactionMore)}
                >
                  <Image src={ThreeDot} alt="three dot" className="h-full w-full " />
                </div>
                {interactionMore && (
                  <DynamicInteraction onHidePost={setIsHidePost} user={user} post={post} />
                )}
              </div>
            </div>
            <div className="flex flex-col items-start gap-[10px]">
              <div className="flex items-center gap-2">
                {/* <span className="text-16 font-semibold text-black">Start auction</span> */}
                <div className="flex items-center gap-3 rounded-[41px] bg-orange-50 px-3 py-1.5">
                  <Link className="flex items-center gap-1.5" href={`/artwork/${asset?.id}`}>
                    <span className="text-14 font-semibold text-orange-500">{asset?.name}</span>
                    <span className="text-14 font-semibold text-black">#{asset?.id}</span>
                  </Link>
                  <div className="-rotate-90">
                    <IconBoldArrow size={12} color="#DA613A" />
                  </div>
                </div>
              </div>
              {asset?.description?.length > 0 && (
                <span className="w-full text-16 font-normal text-gray-700">{text}</span>
              )}
            </div>
          </div>
          <div className="flex w-full items-center justify-center rounded-[20px] bg-[#684238] lg:w-full">
            <div className="w-full">
              <CardRenderFollowType
                isPlayIcon
                disabledClick
                asset={asset}
                isNotBorder
                isAudioBorder={false}
                listSource={ListSource.marketplace_nft}
              />
            </div>
          </div>
          <div className="flex items-start gap-3">
            <TooltipCustom
              content="Sign in to like"
              delay={0}
              closeDelay={0}
              showArrow
              isDisabled={isLogin}
            >
              <div
                className={`flex items-center justify-center gap-2 rounded-[32px] px-3.5 py-2.5 hover:cursor-pointer hover:bg-gray-100 ${
                  isReaction ? 'bg-orange-50' : ''
                }`}
                onClick={onReaction}
              >
                {isReaction ? <IconHeartLiked /> : <IconHeartNotLiked />}
                <span className={`${isReaction ? 'text-[#DA613A]' : 'text-[#181818]'}`}>
                  {totalReactionValue}
                </span>
              </div>
            </TooltipCustom>
            <TooltipCustom
              content="Sign in to comment"
              delay={0}
              closeDelay={0}
              showArrow
              isDisabled={isLogin}
            >
              <div
                className="flex items-center justify-center gap-2 rounded-[32px] px-3.5 py-2.5 hover:cursor-pointer hover:bg-gray-100"
                onClick={onComment}
              >
                <IconComment />
                <span>{totalCommentValue}</span>
              </div>
            </TooltipCustom>
            <SharePost asset={asset} domain={domain} postId={postId} />
          </div>
          {showDetail && (
            <DynamicPostCardDetail
              post={post}
              isOpen={showDetail}
              setShowDetail={setShowDetail}
              isLogin={isLogin}
              isReaction={isReaction}
              onReaction={onReaction}
              onComment={onComment}
              totalCommentValue={totalCommentValue}
              totalReactionValue={totalReactionValue}
              setTotalCommentValue={setTotalCommentValue}
            />
          )}
        </div>
      )}
    </>
  );
};
export default PostCard;
